import { useFeatureFlags } from "@/flags/flags";
import { ProductListingData } from "@/types/productListing";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { loadChunkViaApiRoute } from "./utils/loadChunkViaApiRoute";
import { useRouteParams } from "@/hooks/useRouteParams";
import { useRouter } from "next/router";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { checkMoreChunksAvailable } from "./utils/checkMoreChunksAvailable";
import useRouteLoading from "@/hooks/useRouteLoading";

type Props = {
  children: ReactNode;
  productListingData?: ProductListingData;
};

type ProductListingDataContextType = {
  productListingData?: ProductListingData;
  loadNextChunk: () => void;
  isLoadingChunk?: boolean;
  isChunkLoadError?: boolean;
};

const ProductListingDataContext = createContext<
  ProductListingDataContextType | undefined
>(undefined);

export function ProductListingDataProvider({
  children,
  productListingData: productListingDataFromProps,
}: Props): React.JSX.Element {
  const flags = useFeatureFlags();
  const route = useRouteParams();
  const router = useRouter();
  const [productListingDataState, setProductListingDataState] = useState(
    productListingDataFromProps,
  );
  const [isLoadingChunk, setIsLoadingChunk] = useState(false);
  const [isChunkLoadError, setIsChunkLoadError] = useState(false);
  const isChunkLoadingViaApiRoute = flags?.chunkViaApiRoute?.value === true;
  const isRouteLoading = useRouteLoading();
  const moreChunksAvailable = checkMoreChunksAvailable({
    totalResults: productListingDataState?.results || 0,
    currentChunk: Number(route.query.chunk || 1),
    currentPage: Number(route.query.page || 1),
    pageSize: Number(flags?.pageSize?.value),
    chunkSize: Number(flags?.chunkSize?.value),
  });

  const updateProductListingData = (newChunkData: ProductListingData) =>
    setProductListingDataState((prevState) => ({
      ...prevState,
      ...newChunkData,
      products: [...(prevState?.products || []), ...newChunkData.products],
      analytics: {
        ...prevState?.analytics,
        ...newChunkData.analytics,
      },
    }));

  const loadNextChunk = useCallback(async () => {
    if (
      !productListingDataState ||
      !moreChunksAvailable ||
      isLoadingChunk ||
      isRouteLoading
    ) {
      return;
    }

    const newChunk = Number(route.query.chunk || 1) + 1;

    setIsLoadingChunk(true);
    setIsChunkLoadError(false);

    const shallowRouteToNewChunkUrl = () =>
      router.replace(
        {
          pathname: route.path,
          query: sanitiseQueryParams({
            ...route.query,
            chunk: String(newChunk),
          }),
        },
        undefined,
        { scroll: false, shallow: isChunkLoadingViaApiRoute },
      );

    if (isChunkLoadingViaApiRoute) {
      let productListingDataForChunk: ProductListingData;
      try {
        productListingDataForChunk = await loadChunkViaApiRoute(
          router.query,
          newChunk,
        );
      } catch (error) {
        setIsChunkLoadError(true);
        setIsLoadingChunk(false);
        return;
      }
      await shallowRouteToNewChunkUrl();
      updateProductListingData(productListingDataForChunk);
    } else {
      await shallowRouteToNewChunkUrl();
    }
  }, [
    router,
    route,
    moreChunksAvailable,
    isLoadingChunk,
    isRouteLoading,
    productListingDataState,
    isChunkLoadingViaApiRoute,
  ]);

  useEffect(() => {
    setIsLoadingChunk(false);
  }, [productListingDataState]);

  useEffect(() => {
    setProductListingDataState(productListingDataFromProps);
  }, [productListingDataFromProps]);

  return (
    <ProductListingDataContext.Provider
      value={{
        productListingData: productListingDataState,
        loadNextChunk,
        isLoadingChunk,
        isChunkLoadError,
      }}
    >
      {children}
    </ProductListingDataContext.Provider>
  );
}

export function useProductListingData() {
  const context = useContext(ProductListingDataContext);
  if (!context) {
    throw new Error(
      "useProductListingData must be used within a ProductListingDataProvider",
    );
  }
  return context.productListingData;
}

export function useChunkLoader() {
  const context = useContext(ProductListingDataContext);
  if (!context) {
    throw new Error(
      "useChunkLoader must be used within a ProductListingDataProvider",
    );
  }
  return {
    loadNextChunk: context.loadNextChunk,
    isLoadingChunk: context.isLoadingChunk,
    isChunkLoadError: context.isChunkLoadError,
  };
}
